import { parseData } from '@/api/common';
import { logger } from '@/util/logger';
import {translate} from '@/api/translation';
import { isUserLoggedIn } from '@/util/helpers';
import * as dateAdapter from '@/util/dateAdapter';

const {$} = window;

$.fn.focusWithoutScrolling = function() {
    const yPos = $(document).scrollTop();
    this.focus();
    window.scrollTop = yPos;
};

window.clearTranslationCache = function() {
    if (!window.localStorageSupported) return;
    for (const key in localStorage) {
        if (key.indexOf('tran_') === 0) {
            localStorage.removeItem(key);
        }
    }
};

window.objReusableTimeout = undefined;

// asynchronous translation service returns a promise with CC.X('text') as value
window.async_translate = async function(text, $appendElem) {
    const t = encodeURIComponent(window.stripAllMarkup(text));

    // no translation required for english
    if (window.isEnglish()) {
        return Promise.resolve(text);
    }

    // check localstorage for saved translation
    if (window.localStorageSupported && localStorage[`tran_${  t}`]) {
        return Promise.resolve(localStorage[`tran_${  t}`]);
    }

    const translatedText = await translate(t);

    if ($appendElem && $appendElem.length) {
        $appendElem.text(translatedText);
    }

    if (window.localStorageSupported) {
        localStorage[`tran_${  t}`] = translatedText;
    }

    return translatedText
};

window.stripAllMarkup = function(str) {
    return str.replace(/<[^>]*>|<(?!\s)/g, '');
};

window.setLocalStorage = function(key, value) {
    // this should only be used to store trivial information, as it is unreliable
    
    if (window.localStorageSupported) {
        try {
            localStorage[key] = value;
        } catch (err) {
            logger.warn(`Error trying to set local storage item ${  key}`);
        }
    } else {
        logger.warn('local storage not supported, setting global variable instead');
        window[`ls_${  key}`] = value;
    }
};

window.getLocalStorage = function(key) {
    // this should only be used to retrieve trivial information, as it is unreliable
    if (window.localStorageSupported) {
        try {
            return localStorage[key];
        } catch (err) {
            logger.warn(`Error trying to set local storage item ${  key}`);
        }
    } else {
        return window[`ls_${  key}`];
    }
};

window.verticalScrollPresent = function() {
    return document.documentElement.scrollHeight !== document.documentElement.clientHeight;
};

// ARRAY UTILITIES
Array.prototype.toggle = function(value) {
    const index = this.indexOf(value);

    if (index === -1) {
        this.push(value);
    } else {
        this.splice(index, 1);
    }
};

Array.prototype.move = function (oldIndex, newIndex) {
    // invalid conditions
    if ( this.length === 0  || 
		oldIndex < 0 || 
		oldIndex === newIndex ||
		oldIndex > (this.length - 1) ||
		newIndex > (this.length - 1)) {
        logger.log(`could not move position ${  oldIndex  } to position ${  newIndex  } for array of length ${  this.length}` );
        return this;
    }


    const src = this.concat();
    const srcMap = {};
    srcMap[oldIndex] = newIndex;
    srcMap[newIndex] = oldIndex;


    return this.map(function(val,i){

        const srcIndex = typeof srcMap[i] === 'undefined' ? i : srcMap[i]; 
        return src[srcIndex];
    });
};

// STRING UTILITIES
String.prototype.insertAtIndex = function(index, str) {
    return this.slice(0, index) + str + this.slice(index);
};
String.prototype.cut = function(start, end) {
    return this.substr(0, start) + this.substr(end);
};

if (typeof String.prototype.trim !== 'function') {
    String.prototype.trim = function() {
        return this.replace(/^\s+|\s+$/g, '');
    };
}

// OBJECT UTILITIES
// Object.prototype.deepCopy2 = function(src) {
// 	return {...src};
// };

window.getURLParameter = function(name, url) {
    const searchUrl = url || location.search;
    return decodeURIComponent((new RegExp(`[?|&]${  name  }=` + `([^&;]+?)(&|#|;|$)`).exec(searchUrl) || [null, ''])[1].replace(/\+/g, '%20')) || null;
};

window.isEnterKey = function(e) { return e.keyCode === 13; };
window.isEnterOrSpaceKey = function(e) { return e.keyCode === 13 || e.keyCode === 32; };
window.isTabKey = function(e) { return e.keyCode === 9; };
window.isDownArrowKey = function(e) { return e.keyCode === 40; };
window.isUpArrowKey = function(e) { return e.keyCode === 38; };

// SORTING UTILITIES
window.sortAlpha = function(a, b, comp) {

    if (a === '') return 1;

    if (b === '') return -1;

    if (a === b) return 0;

    if (comp > 0) {
        return a < b ? -1 : 1;
    } else {
        return a < b ? 1 : -1;
    }
};

window.sortAlphaWithSecondary = function(a, b, comp, a2, b2) {
    if (a === b) {
        return window.sortNumeric(a2, b2, comp);
    }

    if (a === '') return 1;

    if (b === '') return -1;

    if (comp > 0) {
        return a < b ? -1 : 1;
    } else {
        return a < b ? 1 : -1;
    }
};

window.sortNumeric = function(a, b, comp) {

    if (isNaN(a)) return 1;

    if (isNaN(b)) return -1;

    if (a === b) return 0;

    if (comp > 0) {
        return a < b ? -1 : 1;
    } else {
        return a < b ? 1 : -1;
    }
};

window.sortNumericWithSecondary = function(a, b, comp, a2, b2) {
    if (a === b) {
        return window.sortNumeric(a2, b2, comp);
    }

    if (isNaN(a)) return 1;

    if (isNaN(b)) return -1;

    if (comp > 0) {
        return a < b ? -1 : 1;
    } else {
        return a < b ? 1 : -1;
    }
};

window.sortNodesByDataAttr = function(nodes, attr, sortDescending) {
    if (!attr) throw new Error('you must specify a property to sort by');

    // default sort mode is ascending. This can be toggled by setting
    // the sortDescending property to true
    const comp = sortDescending ? 1 : -1;


    return nodes.sort(function(a, b) {
        const dataA = $(a).data()[attr];
        const dataB = $(b).data()[attr];

        return window.sortAlpha(dataA, dataB, comp);
    });
};


window.cacheLayoutData = function() {
    // set javascript globals
    window.currentWindowWidth = window.getWindowWidth();
    window.currentDocWidth = document.body.clientWidth;
    window.containerWidth = window.getContainerWidth();
};

window.getWindowWidth = function() {
    return window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
};

window.getContainerWidth = function() {
    const docWidth = document.documentElement.clientWidth;
    const containerWidth = document.getElementById('ContentBody')?.clientWidth;

    if (docWidth < 601) {
        return docWidth - 16 - window.getScrollbarWidth();
    }

    if (docWidth < 900) {
        return docWidth - 26 - window.getScrollbarWidth();
    }

    return Math.ceil(Math.min(docWidth, containerWidth));
};

/*
** Returns the amount of natural horizontal margin of the main content container
*/
window.getContainerMargin = function(){
    // containerWidth less padding
    const containerWidth = getContainerWidth();
    return (document.documentElement.clientWidth - containerWidth);
}


window.getScrollbarWidth = function() {
    const outer = document.createElement('div');
    outer.style.visibility = 'hidden';
    outer.style.width = '100px';
    outer.style.msOverflowStyle = 'scrollbar'; // needed for WinJS apps

    document.body.appendChild(outer);

    const widthNoScroll = outer.offsetWidth;
    // force scrollbars
    outer.style.overflow = 'scroll';
    // add innerdiv
    const inner = document.createElement('div');
    inner.style.width = '100%';
    outer.appendChild(inner);

    const widthWithScroll = inner.offsetWidth;
    // remove divs
    outer.parentNode.removeChild(outer);

    return window.verticalScrollPresent() ? widthNoScroll - widthWithScroll : 0;
};

/**
     * Creates a debounced function that delays invoking `func` until after `wait`
     * milliseconds have elapsed since the last time the debounced function was
     * invoked. The debounced function comes with a `cancel` method to cancel
     * delayed `func` invocations and a `flush` method to immediately invoke them.
     * Provide `options` to indicate whether `func` should be invoked on the
     * leading and/or trailing edge of the `wait` timeout. The `func` is invoked
     * with the last arguments provided to the debounced function. Subsequent
     * calls to the debounced function return the result of the last `func`
     * invocation.
     *
     * **Note:** If `leading` and `trailing` options are `true`, `func` is
     * invoked on the trailing edge of the timeout only if the debounced function
     * is invoked more than once during the `wait` timeout.
     *
     * If `wait` is `0` and `leading` is `false`, `func` invocation is deferred
     * until to the next tick, similar to `setTimeout` with a timeout of `0`.
     *
     * See [David Corbacho's article](https://css-tricks.com/debouncing-throttling-explained-examples/)
     * for details over the differences between `_.debounce` and `_.throttle`.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Function
     * @param {Function} func The function to debounce.
     * @param {number} [wait=0] The number of milliseconds to delay.
     * @param {Object} [options={}] The options object.
     * @param {boolean} [options.leading=false]
     *  Specify invoking on the leading edge of the timeout.
     * @param {number} [options.maxWait]
     *  The maximum time `func` is allowed to be delayed before it's invoked.
     * @param {boolean} [options.trailing=true]
     *  Specify invoking on the trailing edge of the timeout.
     * @returns {Function} Returns the new debounced function.
     * @example
     *
     * // Avoid costly calculations while the window size is in flux.
     * jQuery(window).on('resize', _.debounce(calculateLayout, 150));
     *
     * // Invoke `sendMail` when clicked, debouncing subsequent calls.
     * jQuery(element).on('click', _.debounce(sendMail, 300, {
     *   'leading': true,
     *   'trailing': false
     * }));
     *
     * // Ensure `batchLog` is invoked once after 1 second of debounced calls.
     * var debounced = _.debounce(batchLog, 250, { 'maxWait': 1000 });
     * var source = new EventSource('/stream');
     * jQuery(source).on('message', debounced);
     *
     * // Cancel the trailing debounced invocation.
     * jQuery(window).on('popstate', debounced.cancel);
     */
window.debounce = function (func, wait, options) {
    let lastArgs;
    let lastThis;
    let maxWait;
    let result;
    let timerId;
    let lastCallTime;
    let lastInvokeTime = 0;
    let leading = false;
    let maxing = false;
    let trailing = true;

    if (typeof func !== 'function') {
        throw new TypeError('First parameter to debounce must be a function');
    }
    wait = Number(wait) || 0;

    if (options) {
        leading = !!options.leading;
        maxing = 'maxWait' in options;
        maxWait = maxing ? Math.max(Number(options.maxWait) || 0, wait) : maxWait;
        trailing = 'trailing' in options ? !!options.trailing : trailing;
    }

    function invokeFunc(time) {
        const args = lastArgs;
        const thisArg = lastThis;

        lastArgs = lastThis = undefined;
        lastInvokeTime = time;
        result = func.apply(thisArg, args);
        return result;
    }

    function leadingEdge(time) {
        // Reset any `maxWait` timer.
        lastInvokeTime = time;
        // Start the timer for the trailing edge.
        timerId = setTimeout(timerExpired, wait);
        // Invoke the leading edge.
        return leading ? invokeFunc(time) : result;
    }

    function remainingWait(time) {
        const timeSinceLastCall = time - lastCallTime;
        const timeSinceLastInvoke = time - lastInvokeTime;
        const result = wait - timeSinceLastCall;

        return maxing ? Math.floor(result, maxWait - timeSinceLastInvoke) : result;
    }

    function shouldInvoke(time) {
        const timeSinceLastCall = time - lastCallTime;
        const timeSinceLastInvoke = time - lastInvokeTime;

        // Either this is the first call, activity has stopped and we're at the
        // trailing edge, the system time has gone backwards and we're treating
        // it as the trailing edge, or we've hit the `maxWait` limit.
        return (lastCallTime === undefined || (timeSinceLastCall >= wait) ||
            (timeSinceLastCall < 0) || (maxing && timeSinceLastInvoke >= maxWait));
    }

    function timerExpired() {
        const time = Date.now();
        if (shouldInvoke(time)) {
            return trailingEdge(time);
        }
        // Restart the timer.
        timerId = setTimeout(timerExpired, remainingWait(time));
    }

    function trailingEdge(time) {
        timerId = undefined;

        // Only invoke if we have `lastArgs` which means `func` has been
        // debounced at least once.
        if (trailing && lastArgs) {
            return invokeFunc(time);
        }
        lastArgs = lastThis = undefined;
        return result;
    }

    function cancel() {
        if (timerId !== undefined) {
            clearTimeout(timerId);
        }
        lastInvokeTime = 0;
        lastArgs = lastCallTime = lastThis = timerId = undefined;
    }

    function flush() {
        return timerId === undefined ? result : trailingEdge(Date.now());
    }

    function debounced() {
        const time = Date.now();
        const isInvoking = shouldInvoke(time);

        lastArgs = arguments;
        lastThis = this;
        lastCallTime = time;

        if (isInvoking) {
            if (timerId === undefined) {
                return leadingEdge(lastCallTime);
            }
            if (maxing) {
                // Handle invocations in a tight loop.
                timerId = setTimeout(timerExpired, wait);
                return invokeFunc(lastCallTime);
            }
        }
        if (timerId === undefined) {
            timerId = setTimeout(timerExpired, wait);
        }
        return result;
    }
    debounced.cancel = cancel;
    debounced.flush = flush;
    return debounced;
};
window.lastChangeUserIndex;
window.intCounter = 0;

// *********************************
// POLYFILLS FOR LEGACY BROWSERS
// *********************************
window.localStorageSupported = true;
if (!(window.console)) {
    window.console = {
        log() { },
        debug() { },
        info() { },
        warn() { },
        error() { }
    };
}

// Local and session storage polyfill
// We aren't using it for anything critical, so it is ok for it to just fail silently
// if we don't have access to it.
// Commenting out for ZD# 392043.
/*

try {
    // Test webstorage existence.
    if (!window.localStorage || !window.sessionStorage) throw "exception";
    // Test webstorage accessibility - Needed for Safari private browsing.
    localStorage.setItem('storage_test', 1);
    localStorage.removeItem('storage_test');
} catch (e) {
    window.localStorage = {
        setItem: function () { },
        getItem: function () { },
        removeItem: function () { },
        clear: function () { }
    };
    localStorageSupported = false;

    window.sessionStorage = window.localStorage;
}*/

/* trim ployfill: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/Trim */
if (!String.prototype.trim) {
    (function() {
        // Make sure we trim BOM and NBSP
        const rtrim = /^[\s\uFEFF\xA0]+|[\s\uFEFF\xA0]+$/g;
        String.prototype.trim = function() {
            return this.replace(rtrim, '');
        };
    })();
}

String.prototype.capitalizeFirstLetter = function() {
    return this.charAt(0).toUpperCase() + this.slice(1);
};


// ****************************
//  BROWSER/FEATURE DETECTION
// ****************************

window.isWP = function() {
    if (navigator.userAgent.toLowerCase().indexOf('windows phone') > -1) { return true; } else { return false; }
};

window.isAndroid23 = function() {
    if (navigator.userAgent.toLowerCase().indexOf('android 2.3') > -1) { return true; } else { return false; }
};

window.getIsAndroid = function() {
    try {
        if (window.device && window.device.platform !== 'undefined') {
            return window.device.platform.toLowerCase().indexOf('android') > -1 || navigator.userAgent.toLowerCase().indexOf('android') > -1;
        } else {
            return navigator.userAgent.toLowerCase().indexOf('android') > -1;
        }
    } catch (err) {
        return navigator.userAgent.toLowerCase().indexOf('android') > -1;
    }
};

window.isOldIE = function() {
    let booOld = false;
    for (let i = 2; i <= 9; i++) {
        if (navigator.userAgent.indexOf(`MSIE ${  i  }.`) > -1) {
            booOld = true;
            $('html').addClass('is-old-ie');

        }
    }

    return booOld;
};

window.isTouchDevice = function() {
    let booTouch = false;
    booTouch = (typeof (window.ontouchstart) !== 'undefined');
    if (booTouch === false) {
        if ((navigator.userAgent.match(/Touch/i))) {
            booTouch = true;
        }
    }
    return booTouch;
};

window.loadBrowserIsHighDensity = function() {
    const booIsHighDensity = window.isHighDensity();
    if (booIsHighDensity === true) {
        document.cookie = 'browserIsHighDensity=true;secure';
    }
    else {
        document.cookie = 'browserIsHighDensity=false;secure';
    }
};

window.isHighDensity = function() {
    return ((window.matchMedia && (window.matchMedia('only screen and (min-resolution: 124dpi), only screen and (min-resolution: 1.3dppx), only screen and (min-resolution: 48.8dpcm)').matches || window.matchMedia('only screen and (-webkit-min-device-pixel-ratio: 1.3), only screen and (-o-min-device-pixel-ratio: 2.6/2), only screen and (min--moz-device-pixel-ratio: 1.3), only screen and (min-device-pixel-ratio: 1.3)').matches)) || (window.devicePixelRatio && window.devicePixelRatio > 1.3));
};

window.getIsCertifyMobile = function() {
    try {
        return (window.cordova || window.PhoneGap || window.phonegap)
            && /^file:\/{3}[^\/]/i.test(window.location.href)
            && /ios|iphone|ipod|ipad|android/i.test(navigator.userAgent);
    } catch (e) {
        return false;
    }
};

// This is sort of a legacy function that styles links to have a border-bottom. We don't want
window.applyTextLinkBorderBottom = function () {
    $('#ContentBody a').not(':has(img)').not('.btn').not('.no-border').not('.u-link-subtle').not('.u-link-ghost').not('.top-nav_link').not('.MuiLink-root')
        .add($('.btn-link').not('[disabled]').not('.btn--disabled')).each(function () {
            $(this).hover(function() {
                $(this).addClass('text-link-border-bottom');
            }, function() {
                $(this).removeClass('text-link-border-bottom');
            });
        });
};

// MP: just leaving this here in case it is called somewhere in the code I can't see
window.checkBrowser = function(){};

window.isIE = false;
window.isFF = false;
window.isSafari = false;
window.isChrome = false;
window.isiOS = false;

if (navigator.userAgent.toLowerCase().indexOf('msie') > 0) window.isIE = true;
else if (navigator.userAgent.toLowerCase().indexOf('trident') > 0) window.isIE = true;
else if (navigator.userAgent.toLowerCase().indexOf('firefox') > 0) window.isFF = true; 
else if (navigator.userAgent.toLowerCase().indexOf('chrome') > 0) window.isChrome = true; 
else if (navigator.userAgent.toLowerCase().indexOf('safari') > 0) window.isSafari = true;
if (navigator.userAgent.toLowerCase().indexOf('iphone') > 0 || 
    navigator.userAgent.toLowerCase().indexOf('ipad') > 0) window.isiOS = true;

window.booIsOldIE = window.isOldIE();
window.booIsAndroid23 = window.isAndroid23();
window.booIsWP = window.isWP();
window.booBrowserChecked = true;
window.booTouch = window.isTouchDevice();
window.booIsAndroid = window.getIsAndroid();
window.booIsCertifyMobile = window.getIsCertifyMobile();


// override basic window.alert with a much more pleasing jquery ui dialog
window.alert = function(message, title) {
    window.alertModal.setTitleText(title);
    window.alertModal.setMessageText(message);
    // just say that it was triggered by the body to make the JS happy.
    // these simple alerts don't need to know any information about the node
    // that activated them.
    window.alertModal.open($('body'));
    window.alertModal.$outer.find('.js-ok').focus();
};

window.newCustomConfirm = function(objElement, message, titleText, yesText, noText, isFullPostback) {
    let action = null;
    const makeSingleClick = isFullPostback || false;

    // if these IDs match that means this confirm was triggered by an input node
    // and we have triggered a second click on it to execute it's action by returning true here.
    if (objElement.id && objElement.id === window.confirmModal.activatorID) {
        //activeConfirmTriggerID = '';
        // return true executes action of input node
        return true;
    }

    // Setup Modal
    window.confirmModal.setTitleText(titleText);
    window.confirmModal.setMessageText(message);
    window.confirmModal.setNoText(noText);
    window.confirmModal.setYesText(yesText);

    // Display modal
    window.confirmModal.open($(objElement));

    window.confirmModal.$btnYes.focus();

    // define action that will occur when Yes button is clicked
    action = window.confirmModal.getAction(objElement);

    window.confirmModal.$btnYes.one('click.dialog', function() {
        if (typeof action === 'function') {
            action();

            if (window.confirmModal.postbackTriggered()) {
                // leave modal open if we have triggered a full postback
                window.applyLoadingState();
                window.prm.getInstance().add_endRequest(function delayCloseModal() {
                    window.confirmModal.close();
                    window.prm.getInstance().remove_beginRequest(delayCloseModal);
                });
            } else {
                window.confirmModal.close();
            }
        } else if (typeof action === 'string') {
            window.location = action;

        } else {
            // fire second click event to trigger default action
            if (makeSingleClick == true) {
                $(objElement).addClass('js-click-and-disable');
            }
            $(objElement).trigger('click');
            window.confirmModal.close();
        }
    });

    /* always return false because this function is asynchronous and we want to perform action via a callback. */
    return false;
};

window.customConfirm = function(objElement, message, titleText, yesText, noText, isFullPostback) {
    return window.newCustomConfirm(objElement, message, titleText, yesText, noText, isFullPostback);
};

window.animateAllLoadHighlights = function() {
    // Sometimes we want to highlight element(s) when the page loads to bring attention to them.
    // After a few seconds we remove the highlight since it has served it's purpose.
    $('.a-load-highlight').each(function() {
        // get timeout amount
        window.animateLoadHighlight($(this));
    });
};

window.animateLoadHighlight = function($elem) {
    if ($elem.hasClass('is-animating')) return;
    const t = $elem[0].getAttribute('data-delay') || 3000;

    setTimeout(function() {
        $elem.addClass('animate');

        setTimeout(function() {
            // permanently remove the highlight once the animation completes
            $elem.removeClass('animate');
            $elem.removeClass('is-animating');
            $elem.removeClass(function(index, className) {
                // remove any classes related to this animation pattern
                return (className.match(/a-load-high\S+/g) || []).join(' ');
            });

        }, 500);
    }, t);

    $elem.addClass('is-animating');
};

// ************************************
// Formerly in dateChooserIntel.js
// ************************************
window.senseSourceMonth = function(targetId,sourceId)
{
    let objTarget;
    let objSource;
    objTarget = window.igcal_getCalendarById(targetId);
    objSource = window.igcal_getCalendarById(sourceId);
    if(objTarget && objSource)
    {
        if (objTarget.selDate[0]==-1 && objTarget.selDate[1]==-1 && objSource.selDate[0]!==-1 && objSource.selDate[1]!==-1)
        {
            objTarget.repaint(objSource.selDate[0], objSource.selDate[1]);   
        }
    }
};

window.setTargetMonth = function(targetId,year,month)
{
    let objTarget;
    objTarget = window.igcal_getCalendarById(targetId);
    if(objTarget)
    {
        if (objTarget.selDate[0]==-1 && objTarget.selDate[1]==-1)
        {
            objTarget.repaint(year,month);   
        }
    }
};

window.doSetClientInfoPost = function(docWidth, booReload) {
    document.cookie = `dw=${  docWidth};secure`;

    let firstChar = '';

    // reload page once we have set the cookie so the server can read it
    if (booReload) {
        firstChar = window.location.href.indexOf('?') > -1 ? '&' : '?';
        window.location.href = `${window.location.href + firstChar  }reload=1`;
    }
};

window.doSetClientInfoPostDebounced = window.debounce(function() {
    window.doSetClientInfoPost(window.getWindowWidth());
}, 500);


// simple string hash
window.hash = function(s) {
    let h = 0;
    if (s.length == 0) {
        return h;
    }
    for (let i = 0; i < s.length; i++) {
        const char = s.charCodeAt(i);
        h = ((h<<5)-h)+char;
        h = h & h; // Convert to 32bit integer
    }
    return h.toString();
};
// need this because IE window.scrollY is undefined in IE
window.getYOffset = function() {
    return window.ScrollY || window.pageYOffset;
};

window.formatCurrency = function(amount, code) {
    if (code === 'USD') {
        return `$${  amount}`;
    }

    return `${amount  } ${  code}`;
};

window.dedupeArray = function(arr, key) {
    const seen = {};

    return arr.filter(function(item) {
        return seen.hasOwnProperty(item[key] || item) ? false : (seen[item[key] || item] = true);
    });
};

window.pause = function(t) {
    return new Promise((resolve) => {
        window.setTimeout(resolve,t);
    });
};

window.getLanguageID = function() {
    const el = document.documentElement;
    
    // attribute set in master page code behind
    return el.getAttribute('data-language')
}

window.isEnglish = function() {
    return $('html').hasClass('is-english');
};

window.isUserLoggedIn = isUserLoggedIn;

window.getHash = (string) => {
    let hash = 0, i, chr;
    for (i = 0; i < string.length; i++) {
        chr   = string.charCodeAt(i);
        hash  = ((hash << 5) - hash) + chr;
        hash |= 0; // Convert to 32bit integer
    }
    return hash
}

// Make dateAdapter available globally for legacy pages that can't import it directly.
window.dateAdapter = dateAdapter;

window.getLocaleDateFormat = function(locale) {
    const parts = new Intl.DateTimeFormat(locale).formatToParts(new Date());
    const format = parts
      .map(p => {
        switch (p.type) {
          case 'day': return 'DD';
          case 'month': return 'MM';
          case 'year': return 'YYYY';
          default: return p.value; // likely a separator like '/', '.', or '-'
        }
      })
      .join('');
    return format;
  }